/**
 * Çok dilli madde alanları.
 * `item.lang[]`: gömülü satırlar (örn. multiLang listing ile uyumlu).
 * `langItemList`: itemLang API çıktısı — `{ lang, langItemText, ... }` (yedek).
 * Kök alanlar (`title`, `definition`, `itemText`) Türkçe kanonik kabul edilir.
 */

/** @typedef {'tr' | 'en' | 'fr'} UiLocale */

const LOCALE_TO_LANG_TAGS = /** @type {Record<string, string[]>} */ ({
  tr: ['türkçe', 'turkce', 'turkish', 'tr'],
  en: ['ingilizce', 'english', 'anglais', 'en'],
  fr: [
    'fransızca',
    'fransizca',
    'français',
    'francais',
    'française',
    'francaise',
    'langue française',
    'langue francaise',
    'french',
    'fr',
    'fra',
    'fre',
    'fr-fr',
    'fr_fr',
  ],
});

/** Dil etiketi eşlemesi: aksan + Türkçe özel harfleri tek forma indirger (API “Français”, sözlük “francais”). */
export function normalizeLangLabel(value) {
  let s = String(value ?? '')
    .trim()
    .toLocaleLowerCase('tr')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return s
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i');
}

function langRowMatchesLocale(rawLang, uiLocale) {
  const folded = normalizeLangLabel(rawLang);
  if (!folded) return false;
  const tags = LOCALE_TO_LANG_TAGS[uiLocale] ?? [];
  return tags.some((t) => {
    const nt = normalizeLangLabel(t);
    if (!nt) return false;
    if (folded === nt) return true;
    /* Uzun etiket önekleri: "français (relecture)", "langue française — …" */
    if (nt.length >= 4 && folded.startsWith(nt)) return true;
    return false;
  });
}

function pickItemLangRow(item, uiLocale) {
  const rows = Array.isArray(item?.lang) ? item.lang.filter(Boolean) : [];

  const matching = rows.filter((r) => r?.lang && langRowMatchesLocale(r.lang, uiLocale));
  if (matching.length > 0) {
    const withTitle = matching.find((r) => String(r?.langTitle ?? '').trim());
    return withTitle ?? matching[0];
  }

  /* Yalnızca EN: bazı kayıtlarda lang boş tek satır — FR için kullanılırsa İngilizce yanlış seçilir */
  if (uiLocale === 'en') {
    const unnamed = rows.filter((r) => !String(r?.lang ?? '').trim() && r?.langTitle);
    if (unnamed.length === 1) return unnamed[0];
  }

  return null;
}

function pickLangItemListRow(langItemList, uiLocale) {
  if (!uiLocale || uiLocale === 'tr') return null;
  const list = Array.isArray(langItemList) ? langItemList.filter(Boolean) : [];
  const matching = list.filter((r) => r?.lang && langRowMatchesLocale(r.lang, uiLocale));
  if (matching.length === 0) return null;
  return matching.find((r) => String(r?.langTitle ?? '').trim()) ?? matching[0];
}

/**
 * Seçilen dil için liste/kart başlığı kullanılabilir mi (langTitle dolu mu)?
 * Yalnızca tanım dolu başlık boşsa false döner — itemLang ile başlık tamamlanır.
 */
export function itemHasLocaleRow(item, uiLocale) {
  if (!uiLocale || uiLocale === 'tr') return true;
  const row = pickItemLangRow(item, uiLocale);
  if (!row) return false;
  return String(row?.langTitle ?? '').trim() !== '';
}

/** Boş string, yalnız boşluk veya boş HTML (`<p></p>`, `&nbsp;`) çeviri sayılmaz. */
function isBlankDisplayValue(value) {
  if (value == null) return true;
  const text = String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text === '';
}

function firstFilled(...values) {
  for (const value of values) {
    if (!isBlankDisplayValue(value)) return value;
  }
  return '';
}

/**
 * Kart / liste için görünen başlık ve tanım (gömülü `item.lang` ile).
 */
export function getLocalizedItemLabels(item, uiLocale) {
  const title = item?.title ?? '';
  const definition = item?.definition ?? '';

  if (!uiLocale || uiLocale === 'tr') {
    return { title, definition };
  }

  const row = pickItemLangRow(item, uiLocale);
  return {
    title: firstFilled(row?.langTitle, title),
    definition: firstFilled(row?.langDefinition, row?.definition, definition),
  };
}

/**
 * Madde detayı: başlık, tanım, gövde HTML, kaynak, anahtar kelime — dil seçimine göre.
 * Seçilen dilde satır varsa ama alan boşsa Türkçe köke düşer.
 */
export function getLocalizedArticleDetail(item, uiLocale, langItemList = []) {
  const embeddedRow = uiLocale && uiLocale !== 'tr' ? pickItemLangRow(item, uiLocale) : null;
  const listRow = uiLocale && uiLocale !== 'tr' ? pickLangItemListRow(langItemList, uiLocale) : null;

  if (!uiLocale || uiLocale === 'tr') {
    return {
      title: item?.title ?? '',
      definition: item?.definition ?? '',
      itemText: item?.itemText ?? '',
      sourceText: item?.sourceText ?? '',
      keyword: item?.keyword ?? '',
    };
  }

  return {
    title: firstFilled(embeddedRow?.langTitle, listRow?.langTitle, item?.title),
    definition: firstFilled(
      embeddedRow?.langDefinition,
      embeddedRow?.definition,
      listRow?.langDefinition,
      listRow?.definition,
      item?.definition
    ),
    itemText: firstFilled(
      embeddedRow?.langItemText,
      listRow?.langItemText,
      embeddedRow?.itemText,
      item?.itemText
    ),
    sourceText: firstFilled(
      embeddedRow?.langSourceText,
      listRow?.langSourceText,
      item?.sourceText
    ),
    keyword: firstFilled(
      embeddedRow?.langKeyword,
      listRow?.langKeyword,
      listRow?.keyword,
      item?.keyword
    ),
  };
}

function pickImageLangRow(image, uiLocale) {
  const rows = Array.isArray(image?.docTypeTitleLang) ? image.docTypeTitleLang.filter(Boolean) : [];
  const matching = rows.filter((r) => {
    const label = r?.dil ?? r?.lang;
    return label && langRowMatchesLocale(label, uiLocale);
  });
  if (matching.length === 0) return null;
  return matching.find((r) => String(r?.docTypeTitle ?? '').trim()) ?? matching[0];
}

/**
 * Madde görseli alt yazısı: seçilen dilde `docTypeTitleLang` karşılığı varsa o,
 * yoksa veya boşsa kök `docTypeTitle` (Türkçe).
 */
export function getLocalizedImageCaption(image, uiLocale) {
  const fallback = String(image?.docTypeTitle ?? '').trim();
  if (!image || !uiLocale || uiLocale === 'tr') return fallback;
  const localized = String(pickImageLangRow(image, uiLocale)?.docTypeTitle ?? '').trim();
  return localized || fallback;
}
